import React, { useEffect, useRef, useState, useContext } from 'react';
import AudioProcessor from '../utils/audioProcessor';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import io from 'socket.io-client';
import Peer from 'simple-peer';
import styled, { keyframes, css, createGlobalStyle } from 'styled-components';
import { FaThumbsUp, FaUserFriends, FaComment, FaShareAlt, FaArrowLeft, FaMicrophone, FaMicrophoneSlash, FaVideo, FaVideoSlash, FaRecordVinyl, FaStop, FaTimes, FaArrowRight, FaDesktop, FaComments, FaLock, FaLockOpen, FaFilter } from 'react-icons/fa';
import { SERVER_URL, WS_URL } from '../config';
import { AuthContext } from '../context/AuthContext';
import VideoGrid from './VideoGrid';
import TranscriptionService from '../utils/transcriptionService';
import { FaClosedCaptioning, FaFileDownload } from 'react-icons/fa';



const Stream = () => {
  const { user } = useContext(AuthContext);
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams();
  
  // Get streamId from either location state or URL params
  const streamId = location.state?.streamId || params.streamId;
  const title = location.state?.title || 'Live Stream';
  
  const [socket, setSocket] = useState(null);
  const [viewers, setViewers] = useState([]);
  const [viewerCount, setViewerCount] = useState(0);
  const [likes, setLikes] = useState([]);
  const [likeCount, setLikeCount] = useState(0);
  // Initialize comments as an empty array to prevent the error
  const [comments, setComments] = useState([]);
  const [commentText, setCommentText] = useState('');
  const [isAudioEnabled, setIsAudioEnabled] = useState(true);
  const [isVideoEnabled, setIsVideoEnabled] = useState(true);
  // Add the missing hasLiked state
  const [hasLiked, setHasLiked] = useState(false);
  const [showComments, setShowComments] = useState(false);
  // Add a new state variable to track new viewers for highlighting
  const [newViewers, setNewViewers] = useState([]);
  // Add state for confirmation modal
  const [showEndStreamModal, setShowEndStreamModal] = useState(false);
  const [isScreenSharing, setIsScreenSharing] = useState(false);
  const screenStreamRef = useRef(null);
  
  const videoRef = useRef();
  const streamRef = useRef();
  const [peers, setPeers] = useState({});
  const [participants, setParticipants] = useState([]);
  
  // Add state for permissions
  const [permissions, setPermissions] = useState({
    allowViewerAudio: true,
    allowViewerVideo: true,
    allowViewerChat: true,
    allowViewerScreenShare: true
  });
  
  // Add state for advanced noise cancellation
  const [isAdvancedNoiseCancellationEnabled, setIsAdvancedNoiseCancellationEnabled] = useState(false);
  const audioProcessorRef = useRef(null);
  
  useEffect(() => {
    // Log for debugging
    console.log("Stream component mounted with:", { 
      user, 
      streamId, 
      locationState: location.state,
      params
    });
    
    if (!user || !streamId) {
      console.error("Missing user or streamId");
      navigate('/');
      return;
    }
    
    // Initialize socket connection with improved configuration
    const newSocket = io(WS_URL, {
      transports: ['websocket', 'polling'],
      reconnectionAttempts: 30,
      reconnectionDelay: 1000,
      timeout: 60000,
      autoConnect: true,
      secure: true,
      forceNew: true,
      query: { streamId, username: user.username }
    });
    
    // Add better error handling and logging
    newSocket.on('connect_error', (error) => {
      console.error('Socket connection error:', error.message);
      if (!window.hasShownConnectionAlert) {
        alert(`Connection to server failed. Please check your internet connection and ensure the server is running.`);
        window.hasShownConnectionAlert = true;
        
        setTimeout(() => {
          console.log("Attempting to reconnect...");
          newSocket.connect();
        }, 3000);
      }
    });
    
    newSocket.on('reconnect', (attemptNumber) => {
      console.log(`Socket reconnected after ${attemptNumber} attempts`);
      // Re-join the stream room after reconnection
      newSocket.emit('join-stream', { streamId, username: user.username });
    });
    
    newSocket.on('connect', () => {
      console.log('Socket connected successfully with ID:', newSocket.id);
      // Join stream room immediately after connection
      newSocket.emit('join-stream', { streamId, username: user.username });
    });
    
    setSocket(newSocket);
    
    // Get user media with improved error handling
    const getUserMedia = async () => {
      try {
        if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
          throw new Error('Media devices not supported in this browser');
        }

        // Updated constraints to fix camera direction
        const constraints = {
          video: {
            width: { ideal: 1280 },
            height: { ideal: 720 },
            facingMode: { exact: 'user' }, // Force front camera
            // Add additional constraints to ensure correct orientation
            aspectRatio: { ideal: 1.777777778 }, // 16:9
            frameRate: { ideal: 30 }
          },
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true,
            sampleRate: 48000,
            channelCount: 2,
            // Add advanced noise suppression options
            googEchoCancellation: true,
            googAutoGainControl: true,
            googNoiseSuppression: true,
            googHighpassFilter: true,
            googTypingNoiseDetection: true,
            googNoiseReduction: true
          }
        };

        const stream = await navigator.mediaDevices.getUserMedia(constraints);
        
        if (!stream) {
          throw new Error('Failed to get media stream');
        }

        console.log("Stream obtained successfully:", stream);
        
        // Initialize audio processor
        audioProcessorRef.current = new AudioProcessor();
        await audioProcessorRef.current.initialize(stream);
        
        // Use the original stream initially
        const initialStream = stream;
        
        if (videoRef.current) {
          videoRef.current.srcObject = initialStream;
          // Add CSS transform to ensure correct orientation
          videoRef.current.style.transform = 'scaleX(-1)'; // Mirror the video
        }
        streamRef.current = initialStream;
        
        const audioTrack = initialStream.getAudioTracks()[0];
        const videoTrack = initialStream.getVideoTracks()[0];
        
        setIsAudioEnabled(audioTrack?.enabled || false);
        setIsVideoEnabled(videoTrack?.enabled || false);

        audioTrack?.addEventListener('ended', () => {
          console.log('Audio track ended');
          setIsAudioEnabled(false);
        });

        videoTrack?.addEventListener('ended', () => {
          console.log('Video track ended');
          setIsVideoEnabled(false);
        });

      } catch (error) {
        console.error('Error accessing media devices:', error);
        
        // Try with more lenient constraints if the first attempt fails
        if (error.name === 'OverconstrainedError' || error.name === 'ConstraintNotSatisfiedError') {
          try {
            console.log('Retrying with more lenient constraints...');
            const fallbackStream = await navigator.mediaDevices.getUserMedia({
              video: true,
              audio: true
            });
            
            if (videoRef.current) {
              videoRef.current.srcObject = fallbackStream;
              videoRef.current.style.transform = 'scaleX(-1)';
            }
            streamRef.current = fallbackStream;
            
            const audioTrack = fallbackStream.getAudioTracks()[0];
            const videoTrack = fallbackStream.getVideoTracks()[0];
            
            setIsAudioEnabled(audioTrack?.enabled || false);
            setIsVideoEnabled(videoTrack?.enabled || false);
            
            return;
          } catch (fallbackError) {
            console.error('Fallback media access failed:', fallbackError);
          }
        }
        
        if (error.name === 'NotAllowedError') {
          alert('Camera or microphone access denied. Please check your browser permissions and try again.');
        } else if (error.name === 'NotFoundError') {
          alert('No camera or microphone found. Please connect a device and try again.');
        } else {
          alert('Failed to access camera and microphone. Please check your device connections and permissions.');
        }
      }
    };
    
    getUserMedia();
    
    // Fetch existing comments and check like status
    fetchComments();
    checkLikeStatus();
    
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
      if (newSocket) {
        newSocket.disconnect();
      }
      // Dispose audio processor
      if (audioProcessorRef.current) {
        audioProcessorRef.current.dispose();
      }
    };
  }, [streamId, user, navigate, location.state, params]);

  
  // Handle page refresh/back button when stream is running
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (streamRef.current) {
        e.preventDefault();
        e.returnValue = 'Stream is currently running. Are you sure you want to leave?';
        return e.returnValue;
      }
    };

    const handlePopState = (e) => {
      if (streamRef.current) {
        if (window.confirm('Stream is currently running. Are you sure you want to leave?')) {
          navigate('/');
        } else {
          // Prevent navigation
          window.history.pushState(null, '', window.location.pathname);
        }
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('popstate', handlePopState);
    
    // Push initial state to prevent immediate back
    window.history.pushState(null, '', window.location.pathname);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('popstate', handlePopState);
    };
  }, [navigate]);

  useEffect(() => {
    if (!socket) return;
    
    // Create a map to store peer connections
    const peerConnections = {};
    
    // Listen for new viewers joining with improved error handling
    socket.on('viewer-joined', async ({ viewerId, username }) => {
      console.log(`New viewer joined: ${username} (${viewerId})`);
      
      try {
        if (!streamRef.current) {
          console.error('No local stream available');
          return;
        }

        // Create a new peer connection with improved configuration
        const peer = new Peer({
          initiator: true,
          trickle: false,
          stream: streamRef.current,
          config: {
            iceServers: [
              { urls: 'stun:stun.l.google.com:19302' },
              { urls: 'stun:stun1.l.google.com:19302' }
            ]
          }
        });

        // Add better error handling for peer connection
        peer.on('error', err => {
          console.error('Peer connection error:', err);
          // Attempt to reconnect if possible
          if (peer.destroyed) {
            console.log('Attempting to recreate peer connection...');
            peerConnections[viewerId]?.peer?.destroy();
            delete peerConnections[viewerId];
          }
        });

        peer.on('signal', signal => {
          console.log('Sending signal to viewer:', viewerId);
          socket.emit('sending-signal', { viewerId, signal });
        });

        peer.on('connect', () => {
          console.log('Peer connection established with viewer:', viewerId);
        });
        
        // Add this new event handler to receive the viewer's stream
        peer.on('stream', stream => {
          console.log('Received stream from viewer:', viewerId, username, {
            hasStream: !!stream,
            videoTracks: stream?.getVideoTracks().length,
            audioTracks: stream?.getAudioTracks().length,
            videoEnabled: stream?.getVideoTracks()[0]?.enabled,
            audioEnabled: stream?.getAudioTracks()[0]?.enabled
          });
          
          // Ensure video track is enabled if it exists
          if (stream && stream.getVideoTracks().length > 0) {
            const videoTrack = stream.getVideoTracks()[0];
            if (!videoTrack.enabled) {
              console.log('Enabling disabled video track from viewer');
              videoTrack.enabled = true;
            }
          }
          
          // Update the peer connection with the stream
          peerConnections[viewerId] = {
            peer,
            username,
            stream
          };
          
          // Update the peers state
          setPeers(prevPeers => ({
            ...prevPeers,
            [viewerId]: { peer, username, stream }
          }));
          
          // Update participants list for VideoGrid
          updateParticipantsList(peerConnections);
        });

        // Store the peer connection
        peerConnections[viewerId] = {
          peer,
          username
        };

        // Update the peers state
        setPeers(prevPeers => ({
          ...prevPeers,
          [viewerId]: { peer, username }
        }));

        // Update participants list for VideoGrid
        updateParticipantsList(peerConnections);
      } catch (error) {
        console.error('Error creating peer connection:', error);
      }
    });
    
    // Listen for returned signals with improved error handling
    socket.on('receiving-returned-signal', ({ id, signal }) => {
      console.log(`Received returned signal from viewer ${id}`);
      try {
        if (peerConnections[id]?.peer) {
          peerConnections[id].peer.signal(signal);
        } else {
          console.error('No peer connection found for viewer:', id);
        }
      } catch (error) {
        console.error('Error processing returned signal:', error);
      }
    });

    // Listen for viewer count updates with improved handling
    socket.on('viewer-count-update', ({ count, viewers }) => {
      console.log('Viewer count update:', { count, viewers });
      if (Array.isArray(viewers)) {
        setViewers(viewers);
        setViewerCount(count);
        
        // Find new viewers by comparing current viewers with the updated list
        const currentViewerUsernames = new Set(viewers.map(v => v.username));
        const previousViewerUsernames = new Set(viewers.map(v => v.username));
        
        // Find viewers that are in the new list but not in the previous list
        const newViewerUsernames = Array.from(currentViewerUsernames)
          .filter(username => !previousViewerUsernames.has(username));
        
        if (newViewerUsernames.length > 0) {
          setNewViewers(newViewerUsernames);
          // Clear the highlighting after 2 seconds
          setTimeout(() => setNewViewers([]), 2000);
        }
      }
    });
    
    // Listen for new comments with improved error handling
    socket.on('new-comment', (comment) => {
      console.log('Received new comment:', comment);
      setComments(prevComments => {
        // Ensure prevComments is an array
        const currentComments = Array.isArray(prevComments) ? prevComments : [];
        // Check if comment already exists to prevent duplicates
        const commentExists = currentComments.some(c => 
          c._id === comment._id || 
          (c.username === comment.username && c.text === comment.text && c.timestamp === comment.timestamp)
        );
        return commentExists ? currentComments : [...currentComments, comment];
      });
      // Automatically show comments when a new one is received
      setShowComments(true);
    });
    
    // Add listener for viewer left event
    socket.on('viewer-left', ({ username }) => {
      console.log(`Viewer left: ${username}`);
      // You could add additional UI feedback here if desired
    });
    
    // Listen for like updates with improved handling
    socket.on('like-update', ({ count, likes }) => {
      console.log('Like update:', { count, likes });
      if (Array.isArray(likes)) {
        setLikes(likes);
        setLikeCount(count);
        setHasLiked(likes.some(like => like.username === user.username));
      }
    });
    
    // Add media status update handler
    socket.on('media-status-update', ({ userId, audioMuted, videoMuted }) => {
      console.log('Media status update:', { userId, audioMuted, videoMuted });
      setParticipants(prevParticipants => 
        prevParticipants.map(p => 
          p.id === userId 
            ? { ...p, audioMuted, videoMuted }
            : p
        )
      );
    });
    
    // Listen for viewer screen sharing events
    socket.on('viewer-screen-sharing-started', ({ username }) => {
      console.log(`Viewer ${username} started screen sharing`);
      
      // Add a notification for the host
      const notification = document.createElement('div');
      notification.className = 'screen-sharing-notification';
      notification.textContent = `${username} started screen sharing`;
      document.body.appendChild(notification);
      
      // Remove notification after 3 seconds
      setTimeout(() => {
        if (notification.parentNode) {
          notification.parentNode.removeChild(notification);
        }
      }, 3000);
    });
    
    socket.on('viewer-screen-sharing-stopped', ({ username }) => {
      console.log(`Viewer ${username} stopped screen sharing`);
      
      // Add a notification for the host
      const notification = document.createElement('div');
      notification.className = 'screen-sharing-notification';
      notification.textContent = `${username} stopped screen sharing`;
      document.body.appendChild(notification);
      
      // Remove notification after 3 seconds
      setTimeout(() => {
        if (notification.parentNode) {
          notification.parentNode.removeChild(notification);
        }
      }, 3000);
    });
    
    // Listen for permissions updates from server
    socket.on('permissions-updated', ({ permissions }) => {
      console.log('Permissions updated:', permissions);
      setPermissions(permissions);
    });
    
    // Add error handling for socket events
    socket.on('error', (error) => {
      console.error('Socket error:', error);
    });
    
    // Helper function to update participants list for VideoGrid
    const updateParticipantsList = (peerConnections) => {
      // Create a map to track unique participants by ID
      const uniqueParticipants = new Map();
      
      // Process peer connections and only keep those with streams
      Object.entries(peerConnections).forEach(([id, { username, stream }]) => {
        if (stream) {
          uniqueParticipants.set(id, {
            id,
            username,
            isHost: false,
            audioMuted: false,
            videoMuted: false,
            stream
          });
        }
      });
      
      // Convert map values to array
      const participantsList = Array.from(uniqueParticipants.values());
      
      setParticipants(participantsList);
    };
    
    return () => {
      // Clean up peer connections
      Object.values(peerConnections).forEach(({ peer }) => {
        if (peer && !peer.destroyed) {
          peer.destroy();
        }
      });
      
      socket.off('viewer-joined');
      socket.off('receiving-returned-signal');
      socket.off('viewer-count-update');
      socket.off('new-comment');
      socket.off('like-update');
      socket.off('media-status-update');
      socket.off('permissions-updated');
      socket.off('error');
    };
  }, [socket, user]);
  
  // Replace the existing useEffect that updates participants based on viewers
  // with this improved version that efficiently updates the participants list
  useEffect(() => {
    // Only update if we have peers with streams
    const validPeers = Object.entries(peers)
      .filter(([_, { stream }]) => stream);
    
    if (validPeers.length > 0) {
      // Create a map of current participants by ID for quick lookup
      const currentParticipantsMap = new Map(
        participants.map(p => [p.id, p])
      );
      
      // Process peers and update the map
      validPeers.forEach(([id, { username, stream }]) => {
        currentParticipantsMap.set(id, {
          id,
          username,
          isHost: false,
          audioMuted: false,
          videoMuted: false,
          stream
        });
      });
      
      // Convert map back to array
      setParticipants(Array.from(currentParticipantsMap.values()));
    }
  }, [peers, participants]);
  
  // Modify the fetchComments function to ensure it's called when needed
  const fetchComments = async () => {
    try {
      console.log('Fetching comments for streamId:', streamId);
      const response = await fetch(`${SERVER_URL}/comments/${streamId}`);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Error fetching comments:', response.status, errorText);
        return;
      }
      
      const data = await response.json();
      console.log('Comments fetched successfully:', data);
      setComments(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching comments:', error);
    }
  };
  
  // Add this useEffect to refetch comments when the popup is opened
  useEffect(() => {
    console.log('showComments changed to:', showComments);
    if (showComments) {
      console.log('Fetching comments because showComments is true');
      fetchComments();
    }
  }, [showComments, streamId]);
  
  // Update the sendComment function for better error handling
  const sendComment = async () => {
    if (!commentText.trim() || !socket) {
      console.log('Cannot send empty comment or socket not connected');
      return;
    }

    const commentData = {
      username: user.username,
      text: commentText.trim()
    };

    try {
      // Send via REST API for persistence
      const response = await fetch(`${SERVER_URL}/comments/${streamId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(commentData)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const savedComment = await response.json();
      console.log('Comment saved via API:', savedComment);

      // Emit via socket for real-time updates
      socket.emit('send-comment', {
        streamId,
        ...commentData
      });

      // Remove this line - don't update UI here as it will be updated via socket event
      // This prevents duplicate comments from appearing

      setCommentText('');
    } catch (error) {
      console.error('Error saving comment:', error);
      alert('Failed to save comment. Please try again.');
    }
  };
  
  const handleLike = () => {
    if (!socket || hasLiked) return; // Prevent multiple likes
    
    console.log('Sending like:', {
      streamId,
      username: user.username
    });
    
    socket.emit('send-like', {
      streamId,
      username: user.username
    });
    
    // Optimistically update UI
    setHasLiked(true);
    setLikeCount(prev => prev + 1);
  };
  
  const endStream = () => {
    setShowEndStreamModal(true);
  };
  
  // Add a function to handle the actual stream ending
  const confirmEndStream = () => {
    // Close the modal
    setShowEndStreamModal(false);
    // Navigate to home
    navigate('/');
  };

  const handleShare = () => {
    // Create a modal or dialog to show the stream ID
    alert(`Share this Stream ID: ${streamId}`);
  };
  const copyStreamId = () => {
    const textToCopy = streamId;
    
    // Try using the Clipboard API first
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(textToCopy)
        .then(() => {
          alert('Stream ID copied to clipboard!');
        })
        .catch(err => {
          console.error('Failed to copy using Clipboard API:', err);
          fallbackCopyTextToClipboard(textToCopy);
        });
    } else {
      // Fallback for non-secure contexts or when Clipboard API is not available
      fallbackCopyTextToClipboard(textToCopy);
    }
  };
  
  // Fallback method for copying text
  const fallbackCopyTextToClipboard = (text) => {
    try {
      const textArea = document.createElement('textarea');
      textArea.value = text;
      
      // Make the textarea out of viewport
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      textArea.style.top = '-999999px';
      document.body.appendChild(textArea);
      
      textArea.focus();
      textArea.select();
      
      const successful = document.execCommand('copy');
      document.body.removeChild(textArea);
      
      if (successful) {
        alert('Stream ID copied to clipboard!');
      } else {
        alert('Failed to copy Stream ID. Please copy it manually: ' + text);
      }
    } catch (err) {
      console.error('Fallback copy failed:', err);
      alert('Failed to copy Stream ID. Please copy it manually: ' + text);
    }
  };
  
  const toggleComments = () => {
    console.log('Toggling comments, current state:', showComments);
    setShowComments(!showComments);
  };
  
  // Add recording state variables
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [recordedChunks, setRecordedChunks] = useState([]);
  const mediaRecorderRef = useRef(null);
  const recordingTimerRef = useRef(null);
  
  // Function to start recording
  const startRecording = () => {
    if (!streamRef.current) {
      alert('No stream available to record');
      return;
    }
  
    try {
      console.log("Starting recording with stream:", streamRef.current);

      
      // Check if the stream has active tracks
      const videoTracks = streamRef.current.getVideoTracks();
      const audioTracks = streamRef.current.getAudioTracks();
      
      if (videoTracks.length === 0 && audioTracks.length === 0) {
        alert('No active video or audio tracks found in the stream');
        return;
      }
      
      console.log("Video tracks:", videoTracks.length, "Audio tracks:", audioTracks.length);
      
      // Try different MIME types based on browser support
      const mimeTypes = [
        'video/webm;codecs=vp9,opus',
        'video/webm;codecs=vp8,opus',
        'video/webm',
        'video/mp4'
      ];
      
      let mimeType = '';
      for (const type of mimeTypes) {
        if (MediaRecorder.isTypeSupported(type)) {
          mimeType = type;
          console.log("Using MIME type:", mimeType);
          break;
        }
      }
      
      if (!mimeType) {
        alert('No supported MIME types found for recording');
        return;
      }
      
      // Create the MediaRecorder with the supported MIME type
      const mediaRecorder = new MediaRecorder(streamRef.current, {
        mimeType: mimeType,
        videoBitsPerSecond: 2500000 // 2.5 Mbps
      });
      
      mediaRecorderRef.current = mediaRecorder;
      setRecordedChunks([]);
      
      // Make sure we're capturing data
      mediaRecorder.ondataavailable = (event) => {
        console.log("Data available event, size:", event.data.size);
        if (event.data && event.data.size > 0) {
          setRecordedChunks(prev => {
            console.log("Adding chunk, total chunks:", prev.length + 1);
            return [...prev, event.data];
          });
        }
      };
      
      mediaRecorder.onstart = () => {
        console.log("MediaRecorder started successfully");
      };
      
      mediaRecorder.onerror = (event) => {
        console.error("MediaRecorder error:", event);
        alert('Recording error: ' + event.error);
      };
      
      mediaRecorder.onstop = handleRecordingStop;
      
      // Start the recording - collect data more frequently
      mediaRecorder.start(500); // Collect data every 500ms
      setIsRecording(true);
      
      // Reset recording time
      setRecordingTime(0);
      
      // Clear any existing interval
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
      }
      
      // Start a timer to track recording duration
      recordingTimerRef.current = setInterval(() => {
        setRecordingTime(prevTime => {
          const newTime = prevTime + 1;
          console.log("Recording time updated:", newTime);
          return newTime;
        });
      }, 1000);
      
    } catch (error) {
      console.error('Error starting recording:', error);
      alert('Failed to start recording: ' + error.message);
    }
  };
  
  // Function to stop recording
  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      
      // Make sure to clear the interval when stopping
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
        recordingTimerRef.current = null;
      }
    }
  };
  
  // Handle recording stop and upload
  const handleRecordingStop = async () => {
    setIsRecording(false);
    
    // Clear the timer
    if (recordingTimerRef.current) {
      clearInterval(recordingTimerRef.current);
      recordingTimerRef.current = null;
    }
    
    try {
      // Create a blob from the recorded chunks with explicit video MIME type
      const mimeType = mediaRecorderRef.current?.mimeType || 'video/webm';
      console.log("Using MIME type for blob:", mimeType);
      
      const blob = new Blob(recordedChunks, { type: mimeType });
      
      console.log("Created blob of size:", blob.size, "bytes");
      console.log("Recording duration:", recordingTime, "seconds");
      
      if (blob.size === 0) {
        alert('Recording failed: No data was captured');
        return;
      }
      
      // Create form data for upload with explicit filename and content type
      const formData = new FormData();
      const fileName = `stream_${streamId}_${Date.now()}.webm`;
      
      // Explicitly set the content type for the file
      const file = new File([blob], fileName, { type: mimeType });
      formData.append('video', file);
      
      formData.append('streamId', streamId);
      formData.append('title', title);
      formData.append('username', user.username);
      
      // Make sure to convert recordingTime to string and ensure it's not 0
      const finalDuration = Math.max(recordingTime, 1); // Ensure at least 1 second
      formData.append('duration', String(finalDuration));
      
      console.log("Uploading recording with duration:", finalDuration);
      
      // Upload the recording
      const response = await fetch(`${SERVER_URL}/recordings`, {
        method: 'POST',
        body: formData,
      });
      
      // Handle non-JSON responses (like HTML error pages)
      const contentType = response.headers.get("content-type");
      let responseData;
      
      if (contentType && contentType.includes("application/json")) {
        responseData = await response.json();
      } else {
        const text = await response.text();
        responseData = { message: `Server error: ${response.status} ${response.statusText}`, details: text };
      }
      
      console.log("Server response:", responseData);
      
      if (response.ok) {
        alert('Recording saved successfully!');
      } else {
        throw new Error(responseData.message || 'Failed to save recording');
      }
    } catch (error) {
      console.error('Error saving recording:', error);
      alert('Failed to save recording: ' + error.message);
    }
    
    // Clear recorded chunks
    setRecordedChunks([]);
  };
  
  // Format seconds to MM:SS
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60).toString().padStart(2, '0');
    const secs = Math.floor(seconds % 60).toString().padStart(2, '0');
    return `${mins}:${secs}`;
  };
  
  // Clean up recording on component unmount
  useEffect(() => {
    return () => {
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
      }
      if (mediaRecorderRef.current && isRecording) {
        mediaRecorderRef.current.stop();
      }
    };
  }, [isRecording]);


  
  // Toggle audio
  const toggleAudio = () => {
    if (streamRef.current && streamRef.current.getAudioTracks().length > 0) {
      const audioTrack = streamRef.current.getAudioTracks()[0];
      audioTrack.enabled = !audioTrack.enabled;
      setIsAudioEnabled(audioTrack.enabled);
    }
  };
  
  // Add a function to toggle advanced noise cancellation
  const toggleAdvancedNoiseCancellation = () => {
    if (!audioProcessorRef.current) return;
    
    const newState = !isAdvancedNoiseCancellationEnabled;
    setIsAdvancedNoiseCancellationEnabled(newState);
    
    // Apply or remove advanced processing
    const processedStream = audioProcessorRef.current.enableNoiseReduction(newState);
    
    // Update the stream reference with the processed stream
    if (processedStream) {
      // Keep the video track from the original stream
      const videoTrack = streamRef.current.getVideoTracks()[0];
      
      // Create a new stream with the processed audio and original video
      const newStream = new MediaStream();
      newStream.addTrack(processedStream.getAudioTracks()[0]);
      if (videoTrack) {
        newStream.addTrack(videoTrack);
      }
      
      // Update the stream reference
      streamRef.current = newStream;
      
      // Update all peer connections with the new stream
      Object.values(peers).forEach(peer => {
        if (peer && peer.peer) {
          const senders = peer.peer._pc.getSenders();
          const audioSender = senders.find(sender => 
            sender.track && sender.track.kind === 'audio'
          );
          
          if (audioSender && processedStream.getAudioTracks().length > 0) {
            audioSender.replaceTrack(processedStream.getAudioTracks()[0]);
          }
        }
      });
    }
  };
  
  // Toggle video
  const toggleVideo = () => {
    if (streamRef.current && streamRef.current.getVideoTracks().length > 0) {
      const videoTrack = streamRef.current.getVideoTracks()[0];
      videoTrack.enabled = !videoTrack.enabled;
      setIsVideoEnabled(videoTrack.enabled);
    }
  };
  
  // Add a function to toggle screen sharing
  const toggleScreenSharing = async () => {
    try {
      if (isScreenSharing) {
        // Stop screen sharing
        if (screenStreamRef.current) {
          screenStreamRef.current.getTracks().forEach(track => track.stop());
          screenStreamRef.current = null;
        }
        
        // Restore camera stream to all peers
        if (streamRef.current) {
          Object.keys(peers).forEach(peerId => {
            const peerObj = peers[peerId];
            if (peerObj && peerObj.peer) {
              // Remove all existing tracks
              const senders = peerObj.peer._pc.getSenders();
              senders.forEach(sender => {
                if (sender.track && sender.track.kind === 'video') {
                  peerObj.peer._pc.removeTrack(sender);
                }
              });
              
              // Add camera video track back
              const videoTrack = streamRef.current.getVideoTracks()[0];
              if (videoTrack) {
                peerObj.peer.addTrack(videoTrack, streamRef.current);
              }
            }
          });
        }
        
        // Notify all viewers that screen sharing has stopped
        socket.emit('screen-sharing-stopped', { streamId });
        
        setIsScreenSharing(false);
      } else {
        // Start screen sharing
        const screenStream = await navigator.mediaDevices.getDisplayMedia({
          video: {
            cursor: 'always'
          },
          audio: false
        });
        
        // Handle user canceling the screen share dialog
        screenStream.getVideoTracks()[0].onended = () => {
          toggleScreenSharing();
        };
        
        screenStreamRef.current = screenStream;
        
        // Replace video track with screen sharing track for all peers
        const screenTrack = screenStream.getVideoTracks()[0];
        
        Object.keys(peers).forEach(peerId => {
          const peerObj = peers[peerId];
          if (peerObj && peerObj.peer) {
            // Find video sender and replace track
            const senders = peerObj.peer._pc.getSenders();
            const videoSender = senders.find(sender => 
              sender.track && sender.track.kind === 'video'
            );
            
            if (videoSender) {
              videoSender.replaceTrack(screenTrack);
            } else {
              peerObj.peer.addTrack(screenTrack, screenStream);
            }
          }
        });
        
        // Notify all viewers that screen sharing has started
        socket.emit('screen-sharing-started', { streamId });
        
        setIsScreenSharing(true);
      }
    } catch (error) {
      console.error('Error toggling screen share:', error);
      if (error.name === 'NotAllowedError') {
        alert('Screen sharing permission denied. Please allow screen sharing to continue.');
      } else {
        alert('Failed to toggle screen sharing: ' + error.message);
      }
    }
  };
  
  // Add the checkLikeStatus function
  const checkLikeStatus = async () => {
    try {
      const response = await fetch(`${SERVER_URL}/streams/${streamId}`);
      const data = await response.json();
      if (data.likes && Array.isArray(data.likes)) {
        setHasLiked(data.likes.some(like => like.username === user.username));
      }
    } catch (error) {
      console.error('Error checking like status:', error);
    }
  };
  
  // Add function to toggle permissions
  const togglePermission = (permissionType) => {
    const updatedPermissions = {
      ...permissions,
      [permissionType]: !permissions[permissionType]
    };
    
    // Update local state
    setPermissions(updatedPermissions);
    
    // Send to server
    socket.emit('update-permissions', {
      streamId,
      permissions: updatedPermissions
    });
  };
  // Add these state variables in the component
const [isTranscribing, setIsTranscribing] = useState(false);
const [transcripts, setTranscripts] = useState([]);
const [showTranscripts, setShowTranscripts] = useState(false);
const transcriptionServiceRef = useRef(null);

// Add this after other useEffect hooks
useEffect(() => {
  // Initialize transcription service
  transcriptionServiceRef.current = new TranscriptionService();
  const isSupported = transcriptionServiceRef.current.initialize(
    streamId,
    streamId, // Use streamId as meetingId if not available
    user.username,
    handleTranscriptUpdate
  );
  
  if (!isSupported) {
    console.warn('Speech recognition not supported in this browser');
  }
  
  return () => {
    if (transcriptionServiceRef.current && isTranscribing) {
      transcriptionServiceRef.current.stopTranscription();
    }
  };
}, [streamId, user.username]);

// Add these functions to the component
const handleTranscriptUpdate = (newSegments, isFinal) => {
  if (isFinal) {
    setTranscripts(prev => [...prev, ...newSegments]);
  } else {
    // Update UI with interim results, but don't add to permanent transcript
    // You could add UI state for showing interim results
  }
};

const toggleTranscription = () => {
  if (isTranscribing) {
    transcriptionServiceRef.current.stopTranscription();
    setIsTranscribing(false);
  } else {
    const success = transcriptionServiceRef.current.startTranscription();
    if (success) {
      setIsTranscribing(true);
      setShowTranscripts(true);
    } else {
      alert('Failed to start transcription. Speech recognition may not be supported in this browser.');
    }
  }
};

const downloadTranscript = () => {
  window.open(`${SERVER_URL}/transcriptions/download/${streamId}?format=txt`, '_blank');
};

// Add this to your UI controls section
<ControlItem>
  <MediaButton 
    onClick={toggleTranscription} 
    active={isTranscribing}
    title={isTranscribing ? 'Stop Transcription' : 'Start Transcription'}
  >
    <FaClosedCaptioning />
  </MediaButton>
  <span>Transcription</span>
</ControlItem>

{transcripts.length > 0 && (
  <ControlItem>
    <MediaButton onClick={downloadTranscript} title="Download Transcript">
      <FaFileDownload />
    </MediaButton>
    <span>Download</span>
  </ControlItem>
)}

// Add this to your UI to display transcripts
{showTranscripts && (
  <TranscriptPanel>
    <TranscriptHeader>
      <h3>Live Transcript</h3>
      <CloseButton onClick={() => setShowTranscripts(false)}>
        <FaTimes />
      </CloseButton>
    </TranscriptHeader>
    <TranscriptContent>
      {transcripts.length > 0 ? (
        transcripts.map((segment, index) => (
          <TranscriptSegment key={index}>
            <TranscriptSpeaker>{segment.speakerUsername}</TranscriptSpeaker>
            <TranscriptTime>
              {new Date(segment.timestamp).toLocaleTimeString()}
            </TranscriptTime>
            <TranscriptText>{segment.text}</TranscriptText>
          </TranscriptSegment>
        ))
      ) : (
        <EmptyTranscript>Transcript will appear here...</EmptyTranscript>
      )}
    </TranscriptContent>
  </TranscriptPanel>
)}

// Add these styled components at the bottom of the file
const TranscriptPanel = styled.div`
  position: absolute;
  right: 20px;
  bottom: 80px;
  width: 320px;
  max-height: 400px;
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(10px);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-md);
  display: flex;
  flex-direction: column;
  z-index: 100;
`;

const TranscriptHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 1rem;
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
  
  h3 {
    margin: 0;
    font-size: 1rem;
    font-weight: 600;
  }
`;

const TranscriptContent = styled.div`
  padding: 0.5rem;
  overflow-y: auto;
  max-height: 350px;
`;

const TranscriptSegment = styled.div`
  margin-bottom: 0.75rem;
  padding: 0.5rem;
  background: rgba(255, 255, 255, 0.7);
  border-radius: var(--radius-sm);
`;

const TranscriptSpeaker = styled.div`
  font-weight: 600;
  color: var(--primary);
  font-size: 0.9rem;
`;

const TranscriptTime = styled.div`
  font-size: 0.7rem;
  color: var(--text-secondary);
  margin-bottom: 0.25rem;
`;

const TranscriptText = styled.div`
  font-size: 0.9rem;
`;

const EmptyTranscript = styled.div`
  text-align: center;
  color: var(--text-secondary);
  padding: 2rem 0;
  font-style: italic;
`;


  
  return (
    <StreamContainer>
      <GlobalStyle />
      {/* End Stream Confirmation Modal */}
      {showEndStreamModal && (
        <ModalOverlay>
          <ConfirmationModal>
            <ModalContent>
              <ModalTitle>Are you sure you want to end the Meeting?</ModalTitle>
              <ModalSubtext>This action will disconnect all viewers.</ModalSubtext>
              <ModalButtonGroup>
                <CancelButton onClick={() => setShowEndStreamModal(false)}>
                  Cancel
                </CancelButton>
                <EndStreamButton primary onClick={confirmEndStream}>
                  Yes, End Meeting
                </EndStreamButton>
              </ModalButtonGroup>
            </ModalContent>
          </ConfirmationModal>
        </ModalOverlay>
      )}
      <StreamHeader>
        <EndStreamButton onClick={endStream}>
          <FaArrowLeft /> End Meeting
        </EndStreamButton>
        <StreamTitle><MeetingInfoPrefix>Meeting info:</MeetingInfoPrefix> {title}</StreamTitle>
        <StreamInfo>
          <StreamId>
            Stream ID: {streamId} 
            <CopyButton onClick={copyStreamId}>Copy</CopyButton>
          </StreamId>
          {/* Add viewer count badge in header */}
          <ViewerCountBadge>
            <FaUserFriends />
            <span>{viewerCount}</span>
          </ViewerCountBadge>
        </StreamInfo>
      </StreamHeader>
      
      <MainContent>
        <VideoSection>
          <VideoContainer>
            <VideoGrid 
              participants={participants} 
              localStream={streamRef.current} 
              isHost={true} 
            />
            {isRecording && (
              <RecordingIndicator>
                <FaRecordVinyl />
                <RecordingTime>{formatTime(recordingTime)}</RecordingTime>
              </RecordingIndicator>
            )}
          </VideoContainer>
          
          {/* Keep your existing video element as a fallback but hide it */}
          <video 
            ref={videoRef} 
            autoPlay 
            playsInline 
            muted 
            style={{ display: 'none' }} 
          />
          
          <StreamControls>
            {/* Host Controls */}
            <ControlItem>
              <FaUserFriends />
              <span>{viewerCount} viewers</span>
            </ControlItem>
            <LikeButton onClick={handleLike} liked={hasLiked}>
              <FaThumbsUp />
              <span>{likeCount} likes</span>
            </LikeButton>
            <ControlItem onClick={toggleComments} style={{ cursor: 'pointer' }}>
              <FaComment />
              <span>{Array.isArray(comments) ? comments.length : 0} comments</span>
            </ControlItem>
            
            {/* Permission Controls */}
            <PermissionButton 
              active={permissions.allowViewerAudio} 
              onClick={() => togglePermission('allowViewerAudio')}
            >
              {permissions.allowViewerAudio ? <FaMicrophone /> : <FaMicrophoneSlash />}
              <span>Viewer Audio</span>
            </PermissionButton>
            
            <PermissionButton 
              active={permissions.allowViewerVideo} 
              onClick={() => togglePermission('allowViewerVideo')}
            >
              {permissions.allowViewerVideo ? <FaVideo /> : <FaVideoSlash />}
              <span>Viewer Video</span>
            </PermissionButton>
            
            <PermissionButton 
              active={permissions.allowViewerChat} 
              onClick={() => togglePermission('allowViewerChat')}
            >
              {permissions.allowViewerChat ? <FaComments /> : <FaLock />}
              <span>Viewer Chat</span>
            </PermissionButton>
            
            <PermissionButton 
              active={permissions.allowViewerScreenShare} 
              onClick={() => togglePermission('allowViewerScreenShare')}
            >
              {permissions.allowViewerScreenShare ? <FaDesktop /> : <FaLock />}
              <span>Viewer Screen Share</span>
            </PermissionButton>
            
            {/* Recording Controls */}
            <RecordingControls>
              {isRecording ? (
                <>
                  <RecordingIndicator />
                  <RecordingTime>{formatTime(recordingTime)}</RecordingTime>
                  <MediaButton onClick={stopRecording} title="Stop Recording">
                    <FaStop />
                  </MediaButton>
                </>
              ) : (
                <MediaButton onClick={startRecording} title="Start Recording">
                  <FaRecordVinyl />
                </MediaButton>
              )}
            </RecordingControls>
            
            <MediaControlsContainer>
              <MediaButton 
                onClick={toggleAudio} 
                title={isAudioEnabled ? 'Mute Microphone' : 'Unmute Microphone'}
              >
                {isAudioEnabled ? <FaMicrophone /> : <FaMicrophoneSlash />}
              </MediaButton>
              <MediaButton 
                onClick={toggleVideo} 
                title={isVideoEnabled ? 'Turn Off Camera' : 'Turn On Camera'}
              >
                {isVideoEnabled ? <FaVideo /> : <FaVideoSlash />}
              </MediaButton>
              <MediaButton 
                onClick={toggleAdvancedNoiseCancellation}
                title={isAdvancedNoiseCancellationEnabled ? 'Disable Advanced Noise Cancellation' : 'Enable Advanced Noise Cancellation'}
                style={{ background: isAdvancedNoiseCancellationEnabled ? 'rgba(66, 190, 101, 0.2)' : 'rgba(255, 255, 255, 0.2)' }}
              >
                <div style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
                  <FaMicrophone style={{ color: isAdvancedNoiseCancellationEnabled ? 'var(--success)' : 'var(--text-primary)' }} />
                  <FaFilter style={{ 
                    position: 'absolute', 
                    fontSize: '0.7em', 
                    right: '-5px', 
                    bottom: '-3px',
                    color: isAdvancedNoiseCancellationEnabled ? 'var(--success)' : 'var(--text-primary)' 
                  }} />
                </div>
              </MediaButton>
              <MediaButton 
                onClick={toggleScreenSharing} 
                title={isScreenSharing ? 'Stop Screen Sharing' : 'Share Screen'}
              >
                <FaDesktop />
              </MediaButton>
            </MediaControlsContainer>
            
            <MediaButton 
              onClick={handleShare}
              title="Share Stream"
            >
              <FaShareAlt />
            </MediaButton>
          </StreamControls>
          
          {/* Comment Popup */}
          {showComments && (
            <CommentPopup>
              <CommentPopupHeader>
                <CommentPopupTitle>Comments</CommentPopupTitle>
                <CloseButton onClick={toggleComments}>
                  <FaTimes />
                </CloseButton>
              </CommentPopupHeader>
              <CommentsList>
                {Array.isArray(comments) && comments.map((comment, index) => (
                  <CommentItem key={index}>
                    <CommentUser>{comment.username}</CommentUser>
                    <CommentText>{comment.text}</CommentText>
                  </CommentItem>
                ))}
              </CommentsList>
              <CommentForm onSubmit={(e) => { e.preventDefault(); sendComment(); }}>
                <CommentInput
                  type="text"
                  value={commentText}
                  onChange={(e) => setCommentText(e.target.value)}
                  placeholder="Write a comment..."
                />
                <SendButton type="submit" disabled={!commentText.trim()}>
                  <FaArrowRight />
                </SendButton>
              </CommentForm>
            </CommentPopup>
          )}
        </VideoSection>
        
        <SidePanel>
          <ViewersList>
            <h3><FaUserFriends /> Viewers ({viewerCount})</h3>
            <ul>
              {Array.isArray(viewers) && viewers.length > 0 ? (
                viewers.map((viewer, index) => (
                  <ViewerItem 
                    key={viewer.socketId || index}
                    className={newViewers.includes(viewer.username) ? 'new-viewer' : ''}
                  >
                    {viewer.username}
                  </ViewerItem>
                ))
              ) : (
                <li>No viewers yet</li>
              )}
            </ul>
          </ViewersList>
        </SidePanel>
      </MainContent>
    </StreamContainer>
  );
};

// Modal animations
const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const scaleIn = keyframes`
  from { transform: scale(0.95); opacity: 0; }
  to { transform: scale(1); opacity: 1; }
`;

// Add animations for notifications
const fadeInNotification = keyframes`
  from { opacity: 0; transform: translateY(-20px) translateX(-50%); }
  to { opacity: 1; transform: translateY(0) translateX(-50%); }
`;

const fadeOut = keyframes`
  from { opacity: 1; transform: translateY(0) translateX(-50%); }
  to { opacity: 0; transform: translateY(-20px) translateX(-50%); }
`;

// Add global style for notifications
const GlobalStyle = createGlobalStyle`
  .screen-sharing-notification {
    position: fixed;
    top: 20px;
    left: 50%;
    transform: translateX(-50%);
    background-color: rgba(0, 0, 0, 0.8);
    color: white;
    padding: 10px 20px;
    border-radius: 4px;
    z-index: 1000;
    animation: ${fadeInNotification} 0.3s ease-out forwards, ${fadeOut} 0.3s ease-in forwards 2.7s;
  }
`;

const Button = styled.button`
  padding: 0.75rem 1.5rem;
  border-radius: var(--radius-md);
  font-weight: 500;
  font-size: 1rem;
  cursor: pointer;
  transition: all var(--transition-normal);
  font-family: 'Inter', sans-serif;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: var(--shadow-md);
  }
`;

const StreamContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100vh;
  background: var(--gradient-bg);
`;

const StreamHeader = styled.div`
  display: flex;
  align-items: center;
  padding: 1rem 2rem;
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(10px);
  border-bottom: 1px solid rgba(255, 255, 255, 0.2);
  box-shadow: var(--shadow-sm);
`;

const EndStreamButton = styled(Button)`
  background: var(--error);
  color: white;
  border: none;
  box-shadow: var(--shadow-sm);
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  &:hover {
    background: #e03e48;
    box-shadow: 0 0 15px rgba(250, 77, 86, 0.5);
  }
  
  svg {
    font-size: 1.2rem;
  }
`;

const MeetingInfoPrefix = styled.span`
  font-weight: 700;
  color: var(--primary-color);
  margin-right: 8px;
  font-size: 0.9em;
`;

const StreamTitle = styled.h1`
  margin: 0 auto;
  font-size: 1.6rem;
  font-weight: 600;
  color: var(--text-primary);
  display: flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 50%;
  
  @media (max-width: 768px) {
    font-size: 1.3rem;
    max-width: 60%;
  }
`;

const StreamInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const StreamId = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: rgba(15, 98, 254, 0.1);
  border-radius: var(--radius-md);
  font-size: 0.9rem;
  color: var(--primary);
  
  button {
    color: var(--primary);
    transition: all var(--transition-fast);
    
    &:hover {
      transform: scale(1.1);
    }
  }
`;

const CopyButton = styled.button`
  background: none;
  border: none;
  color: var(--primary);
  cursor: pointer;
  margin-left: 0.5rem;
  font-size: 0.9rem;
  transition: all var(--transition-fast);
  
  &:hover {
    transform: scale(1.1);
    color: var(--primary-dark);
  }
`;

const MainContent = styled.div`
  display: flex;
  flex-grow: 1;
  overflow: hidden;
  position: relative;
`;

const VideoSection = styled.div`
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  padding: 1.5rem;
  position: relative;
`;

const VideoContainer = styled.div`
  flex-grow: 1;
  border-radius: var(--radius-lg);
  overflow: hidden;
  background-color: #000;
  box-shadow: var(--shadow-lg);
  position: relative;
`;

const StreamControls = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 0.5rem; /* Reduced from 0.75rem */
  padding: 0.75rem; /* Reduced from 1rem */
  margin-top: 0.75rem; /* Reduced from 1rem */
  background: rgba(255, 255, 255, 0.9);
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  flex-wrap: wrap;
  
  @media (max-width: 768px) {
    gap: 0.3rem; /* Reduced from 0.5rem */
    padding: 0.5rem; /* Reduced from 0.75rem */
  }
`;

const ControlButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.5rem; /* Reduced size */
  border-radius: 6px; /* Reduced from 8px */
  background: ${props => props.active ? 'rgba(15, 98, 254, 0.1)' : 'rgba(250, 77, 86, 0.1)'};
  color: ${props => props.active ? 'var(--primary)' : 'var(--error)'};
  font-size: 0.9rem; /* Reduced font size */
  transition: all var(--transition-fast);
  box-shadow: var(--shadow-sm);
  
  &:hover {
    transform: scale(1.1);
    box-shadow: var(--shadow-md);
  }
  
  &.record {
    background: ${props => props.active ? 'rgba(250, 77, 86, 0.1)' : 'rgba(15, 98, 254, 0.1)'};
    color: ${props => props.active ? 'var(--error)' : 'var(--primary)'};
  }
  
  &.share {
    background: rgba(66, 190, 101, 0.1);
    color: var(--success);
  }
`;

const ShareButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1.5rem;
  background: rgba(66, 190, 101, 0.1);
  color: var(--success);
  border-radius: var(--radius-md);
  font-weight: 500;
  transition: all var(--transition-fast);
  
  svg {
    font-size: 1.2rem;
  }
  
  &:hover {
    transform: scale(1.05);
    background: rgba(66, 190, 101, 0.2);
  }
`;

const SidePanel = styled.div`
  width: 320px;
  display: flex;
  flex-direction: column;
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(10px);
  border-left: 1px solid rgba(255, 255, 255, 0.2);
  box-shadow: var(--shadow-sm);
  
  @media (max-width: 768px) {
    position: fixed;
    right: 0;
    top: 0;
    bottom: 0;
    z-index: 100;
    transform: ${props => props.isOpen ? 'translateX(0)' : 'translateX(100%)'};
    transition: transform var(--transition-normal);
  }
`;

const ViewersList = styled.div`
  padding: 1.5rem;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  
  h3 {
    margin-top: 0;
    margin-bottom: 1rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 1.1rem;
    color: var(--text-primary);
    
    svg {
      color: var(--primary);
    }
  }
  
  ul {
    list-style: none;
    padding: 0;
    margin: 0;
    max-height: 200px;
    overflow-y: auto;
  }
`;





const StatusBar = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.75rem 1.5rem;
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(5px);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-md);
  z-index: 5;
`;

const StatusItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--text-secondary);
  font-size: 0.9rem;
  
  svg {
    color: var(--primary);
  }
  
  &.likes {
    svg {
      color: #ff4d4f;
    }
  }
`;

const ViewerItem = styled.li`
  display: flex;
  align-items: center;
  padding: 0.75rem 0;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  
  &:last-child {
    border-bottom: none;
  }
  
  &:before {
    content: '';
    display: inline-block;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background-color: var(--success);
    margin-right: 0.75rem;
  }
  
  &.new-viewer {
    animation: highlight 2s ease-out;
  }
  
  @keyframes highlight {
    0% { background-color: rgba(15, 98, 254, 0.1); }
    100% { background-color: transparent; }
  }
`;

const CommentsSection = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  
  h3 {
    margin: 1.5rem 1.5rem 1rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 1.1rem;
    color: var(--text-primary);
    
    svg {
      color: var(--primary);
    }
  }
`;

const CommentsList = styled.div`
  flex-grow: 1;
  overflow-y: auto;
  padding: 0 1.5rem;
`;

const CommentItem = styled.div`
  margin-bottom: 1rem;
  padding: 0.75rem 1rem;
  background: rgba(255, 255, 255, 0.5);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-sm);
`;

const CommentUser = styled.div`
  font-weight: 600;
  margin-bottom: 0.25rem;
  color: var(--primary);
`;

const CommentText = styled.div`
  font-size: 0.9rem;
  color: var(--text-primary);
`;

const CommentTime = styled.div`
  font-size: 0.8rem;
  color: var(--text-tertiary);
  margin-top: 0.25rem;
  text-align: right;
`;

const CommentForm = styled.form`
  display: flex;
  gap: 0.5rem;
  padding: 1rem 1.5rem 1.5rem;
`;

const CommentInput = styled.input`
  flex-grow: 1;
  padding: 0.75rem 1rem;
  border-radius: var(--radius-md);
  border: 1px solid var(--neutral-300);
  font-size: 0.9rem;
  transition: all var(--transition-fast);
  
  &:focus {
    border-color: var(--primary);
    box-shadow: 0 0 0 3px rgba(15, 98, 254, 0.2);
  }
`;

const SendButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: var(--primary);
  color: white;
  transition: all var(--transition-fast);
  
  &:hover {
    background: var(--primary-dark);
    transform: scale(1.1);
  }
  
  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

const MediaControlsContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: rgba(15, 98, 254, 0.1);
  border-radius: var(--radius-md);
`;

const MediaButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.2rem; /* Reduced from original size */
  height: 2.2rem; /* Reduced from original size */
  border-radius: 6px; /* Reduced from 8px */
  background: rgba(255, 255, 255, 0.2);
  color: var(--text-primary);
  font-size: 1rem; /* Reduced font size */
  transition: all var(--transition-fast);
  
  &:hover {
    background: rgba(255, 255, 255, 0.3);
    transform: scale(1.1);
  }
`;

const LikeButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1.5rem;
  background: ${props => props.active ? 'rgba(250, 77, 86, 0.1)' : 'rgba(15, 98, 254, 0.1)'};
  color: ${props => props.active ? '#ff4d4f' : 'var(--primary)'};
  border-radius: var(--radius-md);
  font-weight: 500;
  transition: all var(--transition-fast);
  
  svg {
    font-size: 1.2rem;
  }
  
  &:hover {
    transform: scale(1.05);
    background: ${props => props.active ? 'rgba(250, 77, 86, 0.2)' : 'rgba(15, 98, 254, 0.2)'};
  }
  
  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

const RecordingControls = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: rgba(15, 98, 254, 0.1);
  border-radius: var(--radius-md);
  position: relative;
`;

const RecordingIndicator = styled.div`
  position: absolute;
  top: 1rem;
  right: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: rgba(250, 77, 86, 0.9);
  color: white;
  border-radius: var(--radius-md);
  font-size: 0.9rem;
  font-weight: 500;
  z-index: 5;
  
  svg {
    animation: pulse 1.5s infinite;
  }
  
  @keyframes pulse {
    0% { opacity: 1; }
    50% { opacity: 0.5; }
    100% { opacity: 1; }
  }
`;

const RecordingTime = styled.div`
  font-family: monospace;
  margin: 0 0.5rem;
`;

const ViewerCountBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: rgba(15, 98, 254, 0.1);
  border-radius: var(--radius-md);
  font-size: 0.9rem;
  color: var(--primary);
  
  svg {
    font-size: 1.2rem;
  }
`;

// Add new styled components for the comment popup
const CommentPopup = styled.div`
  position: absolute;
  bottom: 80px;
  right: 20px;
  width: 320px;
  height: 400px;
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(10px);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-lg);
  display: flex;
  flex-direction: column;
  z-index: 100;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.2);
`;

const CommentPopupHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.5rem;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
`;

const CommentPopupTitle = styled.h3`
  margin: 0;
  font-size: 1.1rem;
  font-weight: 600;
  color: var(--text-primary);
`;

const ControlItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.5rem 1.5rem;
  background: rgba(15, 98, 254, 0.1);
  color: var(--primary);
  border-radius: var(--radius-md);
  font-weight: 500;
  transition: all var(--transition-fast);
  
  svg {
    font-size: 1.2rem;
  }
  
  &:hover {
    transform: scale(1.05);
    background: rgba(15, 98, 254, 0.2);
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: var(--text-secondary);
  font-size: 1.2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all var(--transition-fast);
  
  &:hover {
    color: var(--text-primary);
    transform: scale(1.1);
  }
`;

// Modal styled components
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(5px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  animation: ${fadeIn} 0.3s ease;
`;

const ConfirmationModal = styled.div`
  width: 100%;
  max-width: 450px;
  background: rgba(255, 255, 255, 0.85);
  backdrop-filter: blur(10px);
  border-radius: var(--radius-lg);
  overflow: hidden;
  box-shadow: var(--shadow-lg);
  border: 1px solid rgba(255, 255, 255, 0.2);
  animation: ${scaleIn} 0.3s ease;
`;

const ModalContent = styled.div`
  padding: 2rem;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const ModalTitle = styled.h3`
  margin: 0 0 1rem 0;
  color: var(--text-primary);
  font-size: 1.5rem;
  font-weight: 600;
  text-align: center;
  font-family: 'Inter', sans-serif;
`;

const ModalSubtext = styled.p`
  margin: 0 0 2rem 0;
  color: var(--text-secondary);
  text-align: center;
  font-size: 1rem;
  font-family: 'Inter', sans-serif;
`;

const ModalButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  width: 100%;
  justify-content: center;
`;



const CancelButton = styled(Button)`
  background: var(--neutral-200);
  color: var(--text-secondary);
  border: none;
  
  &:hover {
    background: var(--neutral-300);
  }
`;



const HostControlsContainer = styled.div`
  background: rgba(255, 255, 255, 0.9);
  border-radius: 8px;
  padding: 1rem;
  margin-top: 1rem;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
`;

const HostControlsTitle = styled.h3`
  margin-top: 0;
  margin-bottom: 1rem;
  font-size: 1.1rem;
  color: var(--text-primary);
`;

const PermissionControl = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
`;

const PermissionButton = styled.button`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 0.5rem; /* Reduced from 0.75rem */
  border-radius: 6px; /* Reduced from 8px */
  background: ${props => props.active ? 'rgba(15, 98, 254, 0.1)' : 'rgba(250, 77, 86, 0.1)'};
  color: ${props => props.active ? 'var(--primary)' : 'var(--error)'};
  border: 1px solid ${props => props.active ? 'var(--primary)' : 'var(--error)'};
  cursor: pointer;
  transition: all 0.2s;
  
  svg {
    font-size: 1.2rem; /* Reduced from 1.5rem */
    margin-bottom: 0.3rem; /* Reduced from 0.5rem */
  }
  
  span {
    font-size: 0.7rem; /* Reduced from 0.8rem */
    font-weight: 500;
  }
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }
`

export default Stream;
